/* eslint-disable no-unused-vars */
import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import "bootstrap-icons/font/bootstrap-icons.css";

import {
  fetchEmployeeById,
  updateEmployeeById,
  createEmployee,
  clearEmployee,
  checkEmployeeCompleteness,
} from "../../store/slices/employeeDetailSlice";
import { fetchBranchesByCompanyId } from "../../store/slices/branchSlice";
import { fetchCompanyByType } from "../../store/slices/companySlice";
import { fetchDepartments } from "../../store/slices/departmentSlice";
import { fetchJobTitles } from "../../store/slices/jobtitleSlice";
import { fetchClients } from "../../store/slices/clientSlice";
import {
  uploadEmployeeImage,
  fetchEmployeeImageById,
  clearEmployeeImage,
} from "../../store/slices/employeeImageSlice";
import {leaveService} from "../../api/LeaveService/leaveService"
import { Card, CardBody, CardTitle } from "react-bootstrap";
import AllocateLeaveModal from "./LeaveManagement/AllocateLeaveModal";
import LeaveTypeView from "./LeaveManagement/LeaveTypeView";

const Section = ({ title, children, icon }) => (
  <div className="mb-4">
    <div
      className="d-flex align-items-center px-3 py-2 rounded-2 mb-3"
      style={{ backgroundColor: "#1e293b" }}
    >
      <i
        className={`bi ${icon} text-white me-2`}
        style={{ fontSize: "0.85rem" }}
      ></i>
      <h6
        className="m-0 text-white text-uppercase"
        style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
      >
        {title}
      </h6>
    </div>
    <div className="row g-3 px-2">{children}</div>
  </div>
);

const EmployeeLeaveAllocationCard = forwardRef((props, ref) => {
  const { id: propId, onRefresh } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const year = new Date().getFullYear();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState(null);
  const fileInputRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);

  const handleOpen = (leave) => {
    setSelectedLeave(leave); 
    setShowModal(true);
  };

const { data: leaveList = [], isLoading: isLeavesLoading, error: leavesError } = useQuery({
    queryKey: ["employeeLeaveBalances", id, year],
    queryFn: async () => {
      const response = await leaveService.getLeaveBalanceEmployee(id, year);
      return response || [];
    },
    enabled: !!id && id !== 'undefined',
  });

  useEffect(() => {
    if (!id || id === 'undefined') {
      console.warn("Attempted to fetch leaves, but employeeId is missing!");
    }
  }, [id]);

  const { employee } = useSelector((state) => state.employeeDetail);
  const { list: allOrgs = [] } = useSelector((state) => state.companyTypeList);
  const { list: allBranches = [] } = useSelector((state) => state.branch);
  const { list: departments = [] } = useSelector((state) => state.department);
  const { list: jobTitlesList = [] } = useSelector((state) => state.jobtitle);
  const { list: clientList = [] } = useSelector((state) => state.client);
  const image = useSelector((state) => state.employeeImage?.image);

  const [isEditing, setIsEditing] = useState(!id);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [preview, setPreview] = useState(
    "https://placehold.co/200x200?text=Avatar",
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    extension: "",
    employeeId: "",
    employeeStatus: "Active",
    companyEmail: "",
    personalEmail: "",
    companyId: "",
    branchId: "",
    departmentId: "",
    jobTitle: "",
    clientCode: "",
    hiringDate: "",
    resignationDate: "",
    separationDate: "",
    terminationDate: "",
    probationaryDate: "",
    regularizationDate: "",
    remarks: "",
    assignmentType: "Internal",
    clientId: "",
    isSeasonal: false,
  });

  const badgeStyle = (() => {
    const s = (form.employeeStatus || "").toLowerCase();
    if (s === "active" || s === "complete")
      return { bg: "#dcfce7", text: "#15803d", border: "#bbf7d0" };
    if (s === "incomplete" || s === "inactive")
      return { bg: "#fee2e2", text: "#b91c1c", border: "#fecaca" };
    return { bg: "#f1f5f9", text: "#475569", border: "#e2e8f0" };
  })();

  useEffect(() => {
    dispatch(fetchCompanyByType());
    dispatch(fetchBranchesByCompanyId());
    dispatch(fetchJobTitles());
    dispatch(fetchClients());
    if (id) {
      dispatch(fetchEmployeeById(id));
      dispatch(fetchEmployeeImageById(id));
    }
    return () => {
      dispatch(clearEmployee());
      dispatch(clearEmployeeImage());
      hasLoadedRef.current = false;
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (form.companyId) dispatch(fetchDepartments(form.companyId));
  }, [form.companyId, dispatch]);

  useEffect(() => {
    if (employee) {
      const matchedJobTitle = jobTitlesList.find(
        (jt) => jt.id === employee.jobTitleId,
      );

      setForm({
        ...employee,
        firstName: employee.firstName ?? "",
        middleName:
          employee.middleName === "string" || !employee.middleName
            ? ""
            : employee.middleName,
        lastName: employee.lastName ?? "",
        extension:
          employee.extension === "string" || !employee.extension
            ? ""
            : employee.extension,

        companyId: employee.companyId ?? "",
        branchId: employee.branchId ?? "",
        departmentId: employee.departmentId ?? "",
        clientId: employee.clientId ?? "",
        jobTitle: matchedJobTitle ? matchedJobTitle.title : "",
        clientCode: employee.client?.clientCode || "",
        assignmentType: employee.assignmentType ?? "Internal",
        employeeStatus: employee.employeeStatus ?? "Active",
        isSeasonal: employee.isSeasonal === true,
      });
      if (id && !hasLoadedRef.current) {
        hasLoadedRef.current = true;
        setIsEditing(false);
      }
    }
  }, [employee, id, jobTitlesList]);

  useEffect(() => {
    if (image?.imageUrl && !selectedFile) setPreview(image.imageUrl);
  }, [image, selectedFile]);

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  const validate = () => {
    let newErrors = {};
    if (!form.firstName) newErrors.firstName = true;
    if (!form.lastName) newErrors.lastName = true;
    if (!form.companyId) newErrors.companyId = true;
    if (!form.branchId) newErrors.branchId = true;
    if (!form.jobTitle) newErrors.jobTitle = true;

    if (form.assignmentType === "Internal") {
      if (!form.departmentId) newErrors.departmentId = true;
    } else {
      if (!form.clientId) newErrors.clientId = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) {
      setStatusMsg({ text: "Missing required fields.", type: "danger" });
      return;
    }

    setIsSaving(true);
    setStatusMsg({ text: "", type: "" });

    const payload = {
      firstName: form.firstName,
      middleName: form.middleName || "",
      lastName: form.lastName,
      extension: form.extension || "",
      companyId: form.companyId,
      branchId: form.branchId,
      assignmentType: form.assignmentType,
      departmentId:
        form.assignmentType === "Internal" ? form.departmentId : null,
      clientId: form.assignmentType === "Deployed" ? form.clientId : null,
      isSeasonal: form.assignmentType === "Deployed" ? form.isSeasonal : false,
      jobTitleId:
        jobTitlesList.find((jt) => jt.title === form.jobTitle)?.id || null,
      remarks: form.remarks || "",
      employeeStatus: form.employeeStatus,
    };

    try {
      let result;
      let targetId = id;

      if (id) {
        result = await dispatch(
          updateEmployeeById({ id, data: payload }),
        ).unwrap();
      } else {
        result = await dispatch(createEmployee(payload)).unwrap();
        targetId = result.id;
        hasLoadedRef.current = false;
      }

      if (selectedFile) {
        const imageData = new FormData();
        imageData.append("file", selectedFile);
        await dispatch(
          uploadEmployeeImage({ employeeId: targetId, file: imageData }),
        ).unwrap();
        setSelectedFile(null);
      }

      await dispatch(checkEmployeeCompleteness(targetId)).unwrap();

      setStatusMsg({
        text: "Saved and validated successfully!",
        type: "success",
      });
      setIsEditing(false);

      if (!id && targetId) {
        navigate(`/employee/${targetId}`, { replace: true });
      } else {
        dispatch(fetchEmployeeById(targetId));
      }

      setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
    } catch (err) {
      setStatusMsg({
        text: typeof err === "string" ? err : err?.message || "Error saving.",
        type: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container-fluid px-0">
      <style>{`
        .extra-small { font-size: 0.7rem; letter-spacing: 0.02em; }
        .avatar-container { position: relative; width: 150px; height: 150px; margin: 0 auto; }
        .avatar-circle { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; border: 4px solid #fff; box-shadow: 0 4px 15px rgba(0,0,0,0.1); background: #f8fafc; }
        .upload-badge { position: absolute; bottom: 5px; right: 5px; width: 36px; height: 36px; background: #1e293b; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; border: 3px solid #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
        .status-badge-modern { display: inline-block; padding: 6px 14px; border-radius: 8px; font-size: 0.7rem; text-transform: uppercase; background: ${badgeStyle.bg}; color: ${badgeStyle.text}; border: 1.5px solid ${badgeStyle.border}; }
        .progress-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.7); z-index: 9999; display: flex; align-items: center; justify-content: center; border-radius: 8px; }
        
      `}</style>

      <div className="bg-white p-3 rounded-3 shadow-sm border mb-4 position-relative">
        <div
          className="d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mb-4"
          style={{ backgroundColor: "#1e293b", minHeight: "44px" }}
        >
          <div className="d-flex align-items-center">
            <i
              className="bi bi-person-vcard-fill text-white me-2"
              style={{ fontSize: "0.9rem" }}
            ></i>
            <h6
              className="m-0 text-white text-uppercase"
              style={{ fontSize: "0.75rem" }}
            >
              Leave Allocations
            </h6>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row g-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm bg-white">
                <thead className="table-light">
                  <tr>
                    <th>Leave Type</th>
                    <th>Total</th>
                    <th>Booked</th>
                    <th>Used</th>
                    <th>Pending</th>
                    <th>Available</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveList.map((leave) => {
                    const allocated = parseFloat(leave.allocated) || 0;
                    const used = parseFloat(leave.used) || 0;
                    const pending = Math.abs(parseFloat(leave.pending) || 0); 
                    const booked = Math.abs(parseFloat(leave.booked) || 0); 
                    const available = allocated - used - pending;

                    return (
                      <tr key={leave.id}>
                        <td className="fw-bold">{leave.leaveType?.name || "N/A"}</td>
                        <td>{leave.allocated}</td>
                        <td>{leave.booked}</td>
                        <td>{leave.used}</td>
                        <td>{leave.pending}</td>
                        <td className="text-primary fw-bold">{available.toFixed(1)}</td>
                        <td className="text-center">
                          <button 
                            className="btn btn-sm btn-outline-primary fw-bold"
                            style={{ fontSize: "0.75rem", borderRadius: "6px" }}
                            onClick={() => handleOpen(leave)}
                            type="button"
                          >
                            Allocate
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </form>
      </div>
      
      <AllocateLeaveModal 
        isOpen={showModal} 
        onClose={() => {
            setShowModal(false);
            if (onRefresh) onRefresh();
        }} 
        leaveData={selectedLeave}
        onOpenModal={handleOpen}
      />
      
      {isModalOpen && (
        <LeaveTypeView
          employeeId={id} 
          leaveTypeId={selectedLeaveTypeId} 
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
});

export default EmployeeLeaveAllocationCard;